import React, { useEffect, useState } from 'react';
import SideNavigationBar from '../components/SideNavigationBar';
import {
	FileUploadDiv,
	helperText,
	useMainCustomStyle,
} from '../assets/theme/Theme';
import {
	Alert,
	Button,
	ButtonBase,
	CircularProgress,
	Dialog,
	DialogContent,
	Grid,
	List,
	ListItem,
	Switch,
	TextField,
	Typography,
	IconButton as MaterialIconButton,
	Box,
	Chip,
	TableContainer,
	Paper,
	Table,
	TableBody,
	TableRow,
	TableCell,
	Skeleton,
} from '@mui/material';
import Dropzone from 'react-dropzone';
import { CloseIcon, UploadIcon } from '../assets/icons/Icons';
import IconButton from '../components/IconButton';
import { toast } from 'react-toastify';
import {
	addCarePlanTemplate,
	deleteTemplate,
	getCarePlanTemplate,
	getPlanTemplate,
	getSummarizerTemplate,
	storePlanTemplate,
	storeSummaryTemplate,
	storeTemplate,
	updateCarePlanTemplate,
} from '../services/CarePlanServices';
import 'react-toastify/dist/ReactToastify.css';
import { getEnabledUserFeatures } from '../context/MainProvider';
import { FEATURE_TYPES, TOOLTIPS } from '../Utils/Enums';
import { MAIN_THEME_COLOR, WHITE } from '../assets/theme/Colors';
import FileContentTile from '../components/FileContentTile';
import {
	getElderHomeId,
	getSummaryTemplateEnabled,
	setSummaryTemplateEnabled,
} from '../Utils/AuthUtil';
import TextInput from '../components/TextInput';
import { cleanText } from '../Utils/Helpers';

const TABS = {
	ptc: 'Plan Template',
	stc: 'Summarizer Template',
};

const initialLoadingState = {
	loading: false,
	loadingSection: '',
};

const INSTRUCTIONS = {
	plan: [
		'Please upload the plan template file that includes the expected specifications for a resident’s initial care plan',
		'Each plan template should include clear and well-structured headings and subheadings that align with your care home’s care plans and established care standards.',
		'Supporting file format - PDF, TXT',
	],
	summarizer: [
		'Please upload the summarizer template file that includes the expected specifications for a detail summarizer.',
		'Each summarizer template should include clear and well-structured expected summary details that align with your care home and established care standards.',
		'Supporting file format - PDF, TXT',
	],
};

const SUMMARY_TEMP_TABS = {
	rc: 'Resident Summarizer Template',
	cs: 'Client Summarizer Template',
};

function TemplateConfiguration({
	title,
	planTemplates,
	isThisFeatureDisabled,
	name,
	setName,
	nameError,
	setNameError,
	fileData,
	txtError,
	loading,
	deleteTemplate,
	readTextFile,
	onRemoveFiles,
	onUploadPlanTemplate,
	isSummaryTemplateEnabled,
	onChangeSummaryTemplateSetting,
	viewTemplate,
	tempInstructions,
	summaryEndpoints,
	onChangeSummaryEndpoints,
	summaryEndpointsError,
	setSummaryEndpointsError,
	summaryType,
	setSummaryType,
	loadingTemplate,
}) {
	const tempTitle =
		title === 'Summarizer'
			? summaryType === SUMMARY_TEMP_TABS.cs
				? 'Client Summarizer'
				: 'Resident Summarizer'
			: 'Plan';

	return (
		<Grid container paddingLeft="30px" paddingTop={'20px'}>
			<Grid
				container
				width={'100%'}
				flexDirection={'row'}
				justifyContent={'space-between'}
				marginBottom={'10px'}
				rowGap={1}>
				{title === 'Summarizer' && (
					<Grid flexDirection={'row'} columnGap={1}>
						{Object.values(SUMMARY_TEMP_TABS).map((item, index) => (
							<ButtonBase
								key={index}
								onClick={() => setSummaryType(item)}
								sx={{
									height: 38,
									backgroundColor:
										item == summaryType ? '#F9F8F9' : WHITE,
									padding: '5px 24px 5px 24px',
									borderBottom:
										item == summaryType
											? `2px solid ${MAIN_THEME_COLOR}`
											: '',
								}}>
								<Typography
									fontSize={'14px'}
									fontWeight={'500'}
									color={
										item == summaryType
											? MAIN_THEME_COLOR
											: '#686868'
									}>
									{item}
								</Typography>
							</ButtonBase>
						))}
					</Grid>
				)}
				{planTemplates &&
					planTemplates.length > 0 &&
					title === 'Summarizer' && (
						<Grid
							container
							width={{ md: '20%' }}
							flexDirection={'row'}
							border={'1px solid #28696D'}
							paddingX={'5px'}
							paddingY={'2px'}
							borderRadius={'5px'}
							alignItems={'center'}
							justifyContent={'space-between'}>
							<Typography fontSize={'14px'} component="div">
								Enable Summary Templates
							</Typography>
							<Switch
								color="primary"
								name="planTemplatesSwitch"
								checked={isSummaryTemplateEnabled}
								onChange={onChangeSummaryTemplateSetting}
								sx={{
									'& .MuiSwitch-switchBase.Mui-checked': {
										color: MAIN_THEME_COLOR,
									},
									'& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
										{
											backgroundColor: MAIN_THEME_COLOR,
										},
								}}
							/>
						</Grid>
					)}
			</Grid>
			<Grid container paddingBottom={'20px'}>
				{loadingTemplate ? (
					<Skeleton width={'20%'} height={30} />
				) : (
					planTemplates &&
					planTemplates.length > 0 && (
						<Typography
							fontSize={'16px'}
							fontWeight={'600'}
							paddingBottom={'5px'}
							color={'#7C7C7C'}>
							{`Uploaded ${tempTitle} Templates`}
						</Typography>
					)
				)}
				<Grid
					container
					columnGap={2}
					rowGap={2}
					marginTop={{ xs: loadingTemplate ? '5px' : '10px' }}>
					{loadingTemplate ? (
						<Grid container columnGap={2} flexDirection={'row'}>
							<Skeleton width={'20%'} height={80} />
							<Skeleton width={'20%'} height={80} />
						</Grid>
					) : (
						planTemplates &&
						planTemplates.length > 0 &&
						planTemplates.map((template, index) => (
							<Grid
								key={index}
								flexDirection={'row'}
								bgcolor={'#EEF1F7'}
								borderRadius={'5px'}
								padding={'15px 10px 15px 10px'}
								sx={{
									boxShadow: 2,
								}}
								justifyContent={'space-between'}
								alignItems={'center'}
								display={'flex'}>
								<Grid
									item
									width={'80%'}
									flexDirection={'column'}>
									<Typography
										style={{
											wordWrap: 'break-word',
										}}
										color={'#292D32'}
										fontSize={'14px'}>
										{template.template_name}
									</Typography>
								</Grid>
								<Button
									onClick={() =>
										viewTemplate(
											template.template_id,
											title,
										)
									}
									sx={{
										border: '1px solid #28696D',
										paddingLeft: '10px',
										paddingRight: '10px',
										color: '#28696D',
										textTransform: 'none',
										fontSize: '12px',
									}}>
									{'View'}
								</Button>
								<Button
									onClick={() =>
										deleteTemplate(template.template_id)
									}
									sx={{
										border: '1px solid #28696D',
										paddingLeft: '25px',
										paddingRight: '25px',
										color: '#28696D',
										textTransform: 'none',
										marginLeft: '10px',
										fontSize: '12px',
									}}>
									{'Remove'}
								</Button>
							</Grid>
						))
					)}
				</Grid>
			</Grid>
			<Typography
				fontSize={'16px'}
				fontWeight={'600'}
				paddingBottom={'5px'}
				color={'#7C7C7C'}>
				Instruction on file upload
			</Typography>
			<Grid
				container
				sx={{
					backgroundColor: '#FAFAFA',
					marginY: '8px',
					padding: '16px 8px 16px',
					flexDirection: 'column',
					display: 'flex',
				}}>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
					{tempInstructions.map((instruction, index) => (
						<Typography
							key={index}
							component="div"
							sx={{
								fontSize: '14px',
								color: '#324054',
								fontWeight: '600',
								display: 'flex',
								alignItems: 'flex-start',
								'&:before': {
									content: '"• "',
									color: '#324054',
									fontWeight: '500',
									marginRight: 1,
									lineHeight: '16px',
								},
							}}>
							{instruction}
						</Typography>
					))}
				</Box>
			</Grid>

			<Typography
				fontSize={'16px'}
				fontWeight={'600'}
				paddingBottom={'5px'}
				color={'#7C7C7C'}>
				{`Upload ${tempTitle} Templates`}
			</Typography>
			<Grid container flexDirection={'column'} paddingY="5px">
				<Typography
					fontSize={'16px'}
					color={'#7C7C7C'}
					paddingBottom="10px">
					Template Name
				</Typography>
				<TextInput
					error={nameError.status}
					width={'100%'}
					placeholder={'Enter template name'}
					value={name}
					onChange={value => {
						setName(value);
						setNameError({
							status: false,
							msg: '',
						});
					}}
					disabled={isThisFeatureDisabled}
				/>
				<Typography style={helperText}>{nameError.msg}</Typography>
			</Grid>
			{title === 'Summarizer' && (
				<Grid container flexDirection={'column'} paddingY="5px">
					<Typography
						fontSize={'16px'}
						color={'#7C7C7C'}
						paddingBottom="10px">
						Endpoints to consider
					</Typography>
					<TextInput
						width={'100%'}
						error={summaryEndpointsError.status}
						placeholder={'/api1, /api2, /api3'}
						value={summaryEndpoints}
						onChange={value => {
							onChangeSummaryEndpoints(value);
							setSummaryEndpointsError({
								status: false,
								msg: '',
							});
						}}
						disabled={isThisFeatureDisabled}
					/>
					<Typography style={helperText}>
						{summaryEndpointsError.msg}
					</Typography>
				</Grid>
			)}
			<Dropzone onDrop={acceptedFiles => readTextFile(acceptedFiles)}>
				{({ getRootProps, getInputProps }) => (
					<section style={{ width: '100%' }}>
						<div
							{...getRootProps()}
							style={{
								...FileUploadDiv,
								border: txtError?.status
									? '1px dashed red'
									: FileUploadDiv.border,
							}}>
							<input {...getInputProps()} />
							<UploadIcon />
							<br />
							<>
								<Typography
									fontSize={'16px'}
									fontWeight={'700'}
									marginTop={'5px'}
									color={'#7C7C7C'}>
									{`Please attach files that consists of ${title === 'Summarizer' ? 'summary' : 'care plan'} configurations`}
								</Typography>
								<Typography fontSize={'12px'} color={'#B4B2B2'}>
									Supports PDF and TXT files up to 5mb
								</Typography>
							</>
						</div>
					</section>
				)}
			</Dropzone>
			<Typography sx={helperText}>{txtError.msg}</Typography>
			{fileData && (
				<Grid container flexDirection={'column'}>
					<Grid container alignItems="center" spacing={2}>
						<Grid item xs={6} sm={4} md={3} key={0}>
							<FileContentTile
								key={0}
								file={fileData}
								onRemoveFiles={onRemoveFiles}
							/>
						</Grid>
					</Grid>
				</Grid>
			)}
			<Grid
				container
				width={'100%'}
				justifyContent={'flex-end'}
				alignItems={'center'}
				paddingY={'5px'}>
				<Button
					variant="contained"
					style={{
						width: '8%',
						backgroundColor: '#28696D',
						boxShadow: 'none',
						textTransform: 'none',
						paddingTop: '8px',
						paddingBottom: '8px',
						fontSize: '14px',
						fontWeight: '500',
					}}
					onClick={onUploadPlanTemplate}>
					{loading ? (
						<CircularProgress
							sx={{ color: '#FFFFFF' }}
							size={'1.5rem'}
						/>
					) : (
						'Upload'
					)}
				</Button>
			</Grid>
		</Grid>
	);
}

function AddPlanConfiguration() {
	const homeId = getElderHomeId();
	const mainDivStyle = useMainCustomStyle();
	const [txt, setTxt] = useState();
	const [fileData, setFileData] = useState(null);
	const [txtError, setTxtError] = useState({ status: false, msg: '' });
	const [loading, setLoading] = useState(false);
	const { enabledUserFeatureList, isLoading } = getEnabledUserFeatures();
	const isThisFeatureDisabled = !enabledUserFeatureList.includes(
		FEATURE_TYPES[5].key,
	);
	const [planTemplates, setPlanTemplates] = useState([]);
	const [name, setName] = useState('');
	const [nameError, setNameError] = useState({ status: false, msg: '' });
	const [tab, selectedTab] = useState(TABS.ptc);
	const [isSummaryTemplateEnabled, setIsSummaryTemplateEnabled] = useState(
		getSummaryTemplateEnabled(),
	);
	const [planData, setPlanData] = useState([]);
	const [selectedViewPlan, setSelectedViewPlan] = useState('');
	const [selectedViewPlanType, setSelectedViewPlanType] = useState('plan');
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [summaryEndpoints, setSummaryEndpoints] = useState('');
	const [summaryEndpointsError, setSummaryEndpointsError] = useState({
		status: false,
		msg: '',
	});
	const [summaryType, setSummaryType] = useState(SUMMARY_TEMP_TABS.rc);
	const [isFetchingTemplates, setIsFetchingTemplates] = useState(false);

	useEffect(() => {
		if (tab === TABS.stc) {
			fetchSummarizerTemplateFiles(SUMMARY_TEMP_TABS.rc);
		} else {
			fetchCarePlanTemplateFiles();
		}
	}, []);

	async function fetchCarePlanTemplateFiles() {
		setIsFetchingTemplates(true);
		const response = await getPlanTemplate(homeId);

		if (!response.error) {
			setPlanTemplates(response.templates);
		}

		setIsFetchingTemplates(false);
	}

	async function fetchSummarizerTemplateFiles(type) {
		setIsFetchingTemplates(true);
		const scope = type === SUMMARY_TEMP_TABS.cs ? 'client' : 'elder';
		const response = await getSummarizerTemplate(homeId, scope);
		if (!response.error) {
			const temp = response.templates
				.filter(
					item =>
						![
							'narrative_summary',
							'structured_summary',
							'instruction_based_summary',
						].includes(item.template_key),
				)
				.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
				.map(item => {
					return {
						template_name: item.name,
						template_id: item.id,
						content: item.content,
						endpoints: item.selected_endpoints_summariser,
					};
				});
			setPlanTemplates(temp);
		}

		setIsFetchingTemplates(false);
	}

	function readTextFile(e) {
		const ext = e[0].path.split('.').pop().toLowerCase();

		if (!['txt', 'pdf'].includes(ext)) {
			setTxtError({ status: true, msg: 'Invalid file format.' });
			return;
		}

		if (e[0] && e[0].size === 0) {
			setTxtError({
				status: true,
				msg: 'The selected file is empty.',
			});
			return;
		}

		if (e[0] && e[0].size > 5242880) {
			setTxtError({
				status: true,
				msg: 'Maximum upload file size is 5MB.',
			});
			return;
		}

		setTxtError({ status: false, msg: '' });
		setFileData(e[0]);
	}

	function onRemoveFiles() {
		setFileData(null);
	}

	function validateInputs() {
		let isValid = true;
		if (!name || name === '') {
			setNameError({ status: true, msg: 'Enter template name' });
			isValid = false;
		}

		if (!fileData) {
			setTxtError({ status: true, msg: 'Upload the template files' });
			isValid = false;
		}

		if (
			tab === TABS.stc &&
			(!summaryEndpoints || summaryEndpoints === '')
		) {
			setSummaryEndpointsError({
				status: true,
				msg: 'Enter summary endpoints',
			});
			isValid = false;
		}

		return isValid;
	}

	async function onUploadPlanTemplate() {
		if (!validateInputs()) {
			return;
		}

		setLoading(true);
		const formData = new FormData();
		formData.append('template_file', fileData);
		formData.append('client_id', getElderHomeId());
		formData.append('template_name', name);

		const response = await storePlanTemplate(formData);

		if (!response.error) {
			toast.success('Plan template stored', {
				style: {
					background: '#4F9E4E',
				},
			});
			setFileData(null);
			setTxt(null);
			setName('');
			fetchCarePlanTemplateFiles();
		} else {
			toast.error(response.message);
		}

		setLoading(false);
	}

	async function onUploadSummaryTemplate() {
		if (!validateInputs()) {
			return;
		}

		setLoading(true);
		const formData = new FormData();
		formData.append('summary_template_file', fileData);
		formData.append('client_id', getElderHomeId());
		formData.append('name', name);
		formData.append('template_key', name);
		const endpoints = summaryEndpoints.split(',');
		endpoints.forEach(url => {
			formData.append('selected_endpoints_summariser', url);
		});
		formData.append(
			'template_scope',
			summaryType === SUMMARY_TEMP_TABS.cs ? 'client' : 'elder',
		);
		const response = await storeSummaryTemplate(formData);

		if (!response.error) {
			toast.success('Summarizer template stored', {
				style: {
					background: '#4F9E4E',
				},
			});
			setFileData(null);
			setTxt(null);
			setName('');
			setSummaryEndpoints('');
			fetchSummarizerTemplateFiles(summaryType);
		} else {
			toast.error(response.message);
		}

		setLoading(false);
	}

	async function deleteSelectedTemplate(id, type) {
		const route =
			type === 'summarizer'
				? `summary_templates/${id}`
				: `clients/${homeId}/care-templates/${id}`;
		const response = await deleteTemplate(route);
		if (!response.error) {
			toast.success('Template removed successfully', {
				style: {
					background: '#4F9E4E',
				},
			});
			setFileData(null);
			setTxt(null);
			const filterData = planTemplates.filter(
				item => item.template_id !== id,
			);
			setPlanTemplates(filterData);
		} else {
			toast.error(response.message);
		}
	}

	async function onSelect(item) {
		if (item === TABS.stc) {
			fetchSummarizerTemplateFiles();
		} else {
			fetchCarePlanTemplateFiles();
		}
		setTxt(null);
		setFileData(null);
		setTxtError({ status: false, msg: '' });
		setLoading(false);
		setPlanTemplates([]);
		setName('');
		setNameError({ status: false, msg: '' });
		setSummaryEndpointsError({ status: false, msg: '' });
		selectedTab(item);
	}

	function onViewTemplate(id, type) {
		if (type === 'Summarizer') {
			const item = planTemplates.find(item => item.template_id === id);
			setSelectedViewPlan(item.template_name);
			setPlanData(item);
			setSelectedViewPlanType('summary');
		} else {
			const item = planTemplates.find(item => item.template_id === id);
			const plan =
				item.template_config?.care_plan_templates?.initial?.sections;

			setSelectedViewPlan(item.template_name);
			setPlanData(plan);
			setSelectedViewPlanType('plan');
		}
		setIsModalOpen(true);
	}

	const cleanTitle = title => {
		return title.replace(/^\d+\.\s*/, ''); // Removes "14. " or "1. "
	};

	function onSelectSummaryType(type) {
		fetchSummarizerTemplateFiles(type);
		setSummaryType(type);
	}

	function renderTabContent() {
		if (tab === TABS.stc) {
			return (
				<TemplateConfiguration
					title={'Summarizer'}
					planTemplates={planTemplates}
					isThisFeatureDisabled={isThisFeatureDisabled}
					name={name}
					setName={setName}
					nameError={nameError}
					setNameError={setNameError}
					fileData={fileData}
					txtError={txtError}
					loading={loading}
					deleteTemplate={id =>
						deleteSelectedTemplate(id, 'summarizer')
					}
					readTextFile={readTextFile}
					onRemoveFiles={onRemoveFiles}
					onUploadPlanTemplate={onUploadSummaryTemplate}
					isSummaryTemplateEnabled={isSummaryTemplateEnabled}
					onChangeSummaryTemplateSetting={e => {
						const newValue = e.target.checked;
						setIsSummaryTemplateEnabled(newValue);
						setSummaryTemplateEnabled(newValue);
					}}
					viewTemplate={onViewTemplate}
					tempInstructions={INSTRUCTIONS.summarizer}
					summaryEndpoints={summaryEndpoints}
					summaryEndpointsError={summaryEndpointsError}
					setSummaryEndpointsError={setSummaryEndpointsError}
					onChangeSummaryEndpoints={setSummaryEndpoints}
					summaryType={summaryType}
					setSummaryType={onSelectSummaryType}
					loadingTemplate={isFetchingTemplates}
				/>
			);
		} else {
			return (
				<TemplateConfiguration
					title={'Plan'}
					planTemplates={planTemplates}
					isThisFeatureDisabled={isThisFeatureDisabled}
					name={name}
					setName={setName}
					nameError={nameError}
					setNameError={setNameError}
					fileData={fileData}
					txtError={txtError}
					loading={loading}
					deleteTemplate={id => deleteSelectedTemplate(id, 'plan')}
					readTextFile={readTextFile}
					onRemoveFiles={onRemoveFiles}
					onUploadPlanTemplate={onUploadPlanTemplate}
					viewTemplate={onViewTemplate}
					tempInstructions={INSTRUCTIONS.plan}
					loadingTemplate={isFetchingTemplates}
				/>
			);
		}
	}

	return (
		<>
			<SideNavigationBar />
			<div style={mainDivStyle}>
				<Grid
					container
					paddingLeft={{ xs: '0px', md: '20px', sm: '20px' }}
					paddingRight={'23px'}>
					<Grid
						container
						paddingLeft="30px"
						paddingBottom={'20px'}
						flexDirection={'column'}>
						<Typography
							variant="h6"
							fontWeight={'600'}
							color="#28696D">
							Template Configuration Control Panel
						</Typography>
						<Typography sx={{ color: '#5E5C5C', fontSize: '14px' }}>
							Manage your plan and summarizer templates
						</Typography>
					</Grid>
					{isLoading ? (
						<Grid container paddingLeft="30px" paddingTop={'10px'}>
							<Skeleton
								animation="wave"
								width={'100%'}
								height={80}
							/>
							<Skeleton
								animation="wave"
								width={'100%'}
								height={150}
								sx={{ mt: -5 }}
							/>
						</Grid>
					) : isThisFeatureDisabled ? (
						<Grid container paddingLeft="30px" paddingTop={'20px'}>
							<Alert
								severity="warning"
								sx={{
									p: 1.5,
									mb: 2,
									backgroundColor: MAIN_THEME_COLOR,
									color: 'white',
									width: '100%',
									borderRadius: 1,
									fontWeight: 500,
									'& .MuiAlert-icon': {
										color: 'white',
									},
								}}>
								🚀 New Care Plan Generation Instruction Module -
								Coming Soon!
							</Alert>
						</Grid>
					) : (
						<>
							<Grid
								container
								sx={{
									backgroundColor: '#FFFFFF',
									borderRadius: '5px',
									marginLeft: '30px',
									paddingX: '8px',
								}}>
								<Grid
									container
									flexDirection={'row'}
									columnGap={1}
									padding={'10px'}>
									{Object.values(TABS).map((item, index) => (
										<ButtonBase
											key={index}
											onClick={() => onSelect(item)}
											sx={{
												height: 38,
												backgroundColor:
													item == tab
														? '#F9F8F9'
														: WHITE,
												padding: '5px 24px 5px 24px',
												borderBottom:
													item == tab
														? `2px solid ${MAIN_THEME_COLOR}`
														: '',
											}}>
											<Typography
												fontSize={'14px'}
												fontWeight={'500'}
												color={
													item == tab
														? MAIN_THEME_COLOR
														: '#686868'
												}>
												{item}
											</Typography>
										</ButtonBase>
									))}
								</Grid>
							</Grid>
							<Grid
								container
								sx={{
									backgroundColor: '#FFFFFF',
									borderRadius: '5px',
									marginLeft: '30px',
									marginTop: '10px',
									paddingRight: '8px',
									paddingBottom: '8px',
								}}>
								{renderTabContent()}
							</Grid>
						</>
					)}
				</Grid>
				<Dialog
					fullWidth
					open={isModalOpen}
					onClose={() => setIsModalOpen(!isModalOpen)}
					scroll="body">
					<DialogContent style={{ padding: '0px 0px 20px 0px' }}>
						<Grid
							container
							sx={{ boxShadow: 3 }}
							padding={'10px'}
							flexDirection={'row'}
							justifyContent={'space-between'}
							alignItems={'center'}>
							<Typography fontWeight={'600'}>
								Plan Template : {selectedViewPlan}
							</Typography>
							<MaterialIconButton
								style={{ padding: '0' }}
								onClick={() => setIsModalOpen(false)}>
								<CloseIcon />
							</MaterialIconButton>
						</Grid>
						<Grid container padding={'10px'}>
							{selectedViewPlanType === 'summary' ? (
								<Box sx={{ mb: 2 }}>
									<Box sx={{ p: 2 }}>
										<Typography
											variant="body2"
											color="text.secondary"
											gutterBottom>
											Content:
										</Typography>
										<Typography
											size="small"
											sx={{
												mr: 1,
												mb: 1,
												fontSize: '14px',
												whiteSpace: 'pre-line',
											}}
											variant="outlined">
											{planData.content}
										</Typography>
									</Box>
									{planData.endpoints &&
										planData.endpoints.length > 0 && (
											<Box sx={{ p: 2 }}>
												<Typography
													variant="body2"
													color="text.secondary"
													gutterBottom>
													Endpoints:
												</Typography>
												<Typography
													size="small"
													sx={{
														mr: 1,
														mb: 1,
														fontSize: '14px',
													}}
													variant="outlined">
													{planData.endpoints}
												</Typography>
											</Box>
										)}
								</Box>
							) : (
								<Box sx={{ mb: 2 }}>
									{planData.map((item, index) => (
										<Box key={index} sx={{ mb: 4 }}>
											<Typography
												variant="h6"
												gutterBottom>
												{cleanTitle(item.display_title)}
											</Typography>

											<Box sx={{ mb: 3 }}>
												<Typography
													variant="body2"
													color="text.secondary"
													gutterBottom>
													Instructions:
												</Typography>
												{item.instructions.map(
													(instr, i) => (
														<Typography
															key={i}
															size="small"
															sx={{
																mr: 1,
																mb: 1,
																fontSize:
																	'14px',
															}}
															variant="outlined">
															{cleanText(instr)}
														</Typography>
													),
												)}
											</Box>

											<Typography
												variant="body2"
												color="text.secondary"
												gutterBottom>
												Sections:
											</Typography>
											<TableContainer
												component={Paper}
												elevation={2}>
												<Table size="small">
													<TableBody>
														{Object.entries(
															item.output_schema,
														).map(
															([key, value]) => (
																<TableRow
																	key={key}>
																	<TableCell
																		sx={{
																			fontWeight:
																				'medium',
																			minWidth: 200,
																		}}>
																		{key}
																	</TableCell>
																</TableRow>
															),
														)}
													</TableBody>
												</Table>
											</TableContainer>
										</Box>
									))}
								</Box>
							)}
						</Grid>
					</DialogContent>
				</Dialog>
			</div>
		</>
	);
}

export default AddPlanConfiguration;
